/**
 * SetPickerMenu — "Load set" popover listing the library's sets of one kind
 * (name, updated date, usage line). Replace-confirm handled by the caller's
 * onPick via ConfirmDialog — never window.confirm.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { FolderOpen } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';

import { useLibrarySets } from '../api/hooks/useLibrarySets';
import { useLibraryUsage } from '../api/hooks/useLibraryUsage';
import type { LibrarySet, LibrarySetKind } from '../api/client/sets-api';

export interface SetPickerMenuProps {
  kind: LibrarySetKind;
  onPick: (set: LibrarySet) => void;
}

function formatUpdated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

const SetPickerMenu: FC<SetPickerMenuProps> = ({ kind, onPick }) => {
  const [open, setOpen] = useState(false);
  const sets = useLibrarySets(kind, open);
  const usage = useLibraryUsage(open);
  const bucket = kind === 'formula' ? usage.data?.formulaSets : usage.data?.ruleSets;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 px-2.5 text-xs">
          <FolderOpen className="mr-1 h-3.5 w-3.5" aria-hidden />
          Load set
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-[140] w-80 rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in slide-in-from-top-1"
        >
          {sets.isLoading ? (
            <p className="px-2 py-2 text-[11px] text-muted-foreground">Loading…</p>
          ) : (sets.data ?? []).length === 0 ? (
            <p className="px-2 py-2 text-[11px] italic text-muted-foreground">
              No saved sets yet — save this tab&apos;s work to start the library.
            </p>
          ) : (
            <ul className="max-h-64 overflow-y-auto">
              {(sets.data ?? []).map((set) => (
                <li key={set.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick(set);
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-accent hover:text-accent-foreground"
                  >
                    {set.color ? (
                      <span
                        aria-hidden
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: set.color }}
                      />
                    ) : (
                      <FolderOpen className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                    )}
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-medium">{set.name}</span>
                      <span className="truncate text-[10px] text-muted-foreground">
                        {bucket?.[set.id]
                          ? `${bucket[set.id].packages} packages · ${bucket[set.id].campaigns} campaigns · `
                          : ''}
                        updated {formatUpdated(set.updatedAt)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default SetPickerMenu;
